/**
 * Centralized color definitions for the application.
 * 
 * DESIGN PRINCIPLE: Semantic colors that convey meaning, not use-case specific colors.
 * All UI elements should derive their colors from these semantic categories.
 */
import { paletteScales as elementa } from '@/design-system/elementa/palette';

// ============================================================================
// BRAND COLORS - Capio brand identity (main schema)
// ============================================================================

export const brand = {
  primary: elementa.primary[800],
  secondary: elementa.secondary[500],
  positive: elementa.success[600],
  warning: elementa.warning[600],
  negative: elementa.error[600],
  magic: elementa.magic[500],
  sidebar: elementa.primary[950],
  accent: elementa.accent[600],
} as const;

// ============================================================================
// SEMANTIC COLORS - Convey meaning through color
// Each has: main (icons, text), light (backgrounds), dark (hover/emphasis)
// ============================================================================

export const semantic = {
  success: {
    main: elementa.success[600],
    light: elementa.success[100],
    dark: elementa.success[700],
  },
  warning: {
    main: elementa.warning[600],
    light: elementa.warning[100],
    dark: elementa.warning[700],
  },
  error: {
    main: elementa.error[600],
    light: elementa.error[100],
    dark: elementa.error[700],
  },
  info: {
    main: elementa.info[600],
    light: elementa.info[100],
    dark: elementa.info[700],
  },
  neutral: {
    main: elementa.neutral[500],
    light: elementa.neutral[100],
    dark: elementa.neutral[600],
  },
} as const;

// ============================================================================
// GREY SCALE - For text, borders, disabled states
// ============================================================================

export const grey = {
  50: elementa.neutral[50],
  100: elementa.neutral[100],
  200: elementa.neutral[200],
  300: elementa.neutral[300],
  400: elementa.neutral[400],
  500: elementa.neutral[500],
  600: elementa.neutral[600],
  700: elementa.neutral[700],
  800: elementa.neutral[800],
  900: elementa.neutral[900],
} as const;

// ============================================================================
// SURFACE COLORS - Backgrounds and surfaces (derived from grey palette)
// ============================================================================

export const surface = {
  page: elementa.neutral[50],
  card: elementa.neutral[0],
  hover: elementa.neutral[100],
  code: elementa.neutral[800],
} as const;

// ============================================================================
// GRADIENTS - Derived from brand colors
// ============================================================================

export const gradients = {
  brand: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.positive} 100%)`,
  brandReverse: `linear-gradient(135deg, ${brand.positive} 0%, ${brand.primary} 100%)`,
} as const;

// ============================================================================
// SHADOWS - Derived from brand colors
// ============================================================================

export const shadows = {
  brand: '0 4px 12px rgba(17, 66, 15, 0.4)',
} as const;

// ============================================================================
// CATEGORICAL COLORS - For data visualization (topics, categories, charts)
// Each category has: bg (light background), color (text/icon)
// ============================================================================

export const categories = {
  indigo: { bg: '#e0e7ff', color: '#4338ca' },
  amber: { bg: '#fef3c7', color: '#b45309' },
  purple: { bg: '#f3e8ff', color: '#7c3aed' },
  green: { bg: '#dcfce7', color: '#16a34a' },
  red: { bg: '#fee2e2', color: '#dc2626' },
  pink: { bg: '#fce7f3', color: '#be185d' },
  neutral: { bg: '#f1f5f9', color: '#475569' },
} as const;

// ============================================================================
// COLOR SCALES - For gradients in charts and progress bars
// ============================================================================

export const scales = {
  // Blue scale for info-related visualizations (lightest to darkest)
  info: ['#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb'],
  // Green scale for heatmaps and activity visualizations (lightest to darkest)
  // Based on brand.accent (#007500) - 6 colors for heatmap granularity
  green: ['#EEF7ED', '#C5E1C3', '#8CC689', '#4DAB4A', '#2D8F2D', '#007500'],
} as const;

// ============================================================================
// SPECIAL CASES - Documented exceptions (minimize these)
// ============================================================================

export const special = {
  // Matches the portal background so the login page reads as the same surface
  loginBg: elementa.neutral[50],
  // Login page dotted pattern
  loginDot: elementa.neutral[300],
  // Highlight for citation references (yellow)
  highlight: '#facc15',
  // Online status indicator (bright green)
  online: '#4ade80',
} as const;

// ============================================================================
// Z-INDEX SCALE - Centralized layering to prevent conflicts
// MUI defaults: Drawer=1200, Modal=1300, Snackbar=1400, Tooltip=1500
// ============================================================================

export const zIndex = {
  /** Main sidebar navigation */
  sidebar: 1200,
  /** Chat widget floating button and drawer */
  chatWidget: 1300,
  /** Co-Pilot floating button and drawer (above chat widget) */
  copilot: 1350,
  /** Modals and dialogs - use MUI default or this value */
  modal: 1300,
  /** Toast notifications */
  snackbar: 1400,
  /** Tooltips - highest priority */
  tooltip: 1500,
} as const;
